import { useState, useMemo } from 'react';
import { UserPlus, UserMinus, Search, Loader2, AlertCircle, Package, ShieldCheck, Truck } from 'lucide-react';
import { useShipAccess, type Role } from '../../hooks/useShipAccess';
import allUsers from '../../data/onelogin-users.json';

const ROLE_CONFIG: Record<Role, { label: string; icon: React.ReactNode; badge: string; border: string; text: string }> = {
  admin: {
    label: 'Admin',
    icon: <ShieldCheck className="w-3 h-3" />,
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  'ship-request': {
    label: 'Ship Request',
    icon: <Truck className="w-3 h-3" />,
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    border: 'border-teal-200',
    text: 'text-teal-700',
  },
};

function RoleToggle({
  value,
  onChange,
  disabled,
}: {
  value: Role;
  onChange: (r: Role) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs">
      {(['ship-request', 'admin'] as Role[]).map((r) => (
        <button
          key={r}
          type="button"
          disabled={disabled}
          onClick={() => onChange(r)}
          className={`flex items-center gap-1 px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
            value === r
              ? `${ROLE_CONFIG[r].badge} font-medium`
              : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          {ROLE_CONFIG[r].icon}
          {ROLE_CONFIG[r].label}
        </button>
      ))}
    </div>
  );
}

export function ShipAccessPanel() {
  const { users, loading, error, saving, removing, addUser, updateRole, removeUser } = useShipAccess();
  const [search, setSearch] = useState('');
  const [pendingRoles, setPendingRoles] = useState<Record<string, Role>>({});

  const grantedEmails = useMemo(() => new Set(users.map((u) => u.email.toLowerCase())), [users]);

  /**
   * Built from the granted list (the database), not from the OneLogin snapshot —
   * `onelogin-users.json` is a build-time file, so anyone granted access before the
   * next `npm run sync:users` would otherwise be invisible here and undercount the
   * total, with nothing on screen to say so. The snapshot only supplies display names.
   */
  const grantedUsers = useMemo(
    () =>
      users
        .map((a) => {
          const known = allUsers.find((u) => u.email.toLowerCase() === a.email.toLowerCase());
          return {
            email: a.email,
            name: known?.name ?? a.email.split('@')[0],
            role: a.role,
          };
        })
        .sort((a, b) => {
          if (a.role === b.role) return a.name.localeCompare(b.name);
          return a.role === 'admin' ? -1 : 1;
        }),
    [users],
  );

  // Available users (not yet granted) filtered by search
  const availableUsers = useMemo(() => {
    const q = search.toLowerCase().trim();
    return allUsers.filter((u) => {
      if (grantedEmails.has(u.email.toLowerCase())) return false;
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [search, grantedEmails]);

  // Default add-role for each user (stored in local state until Add is clicked)
  function getPendingRole(email: string): Role {
    return pendingRoles[email] ?? 'ship-request';
  }

  function setPendingRole(email: string, role: Role) {
    setPendingRoles((prev) => ({ ...prev, [email]: role }));
  }

  async function handleAdd(email: string) {
    await addUser(email, getPendingRole(email));
    setPendingRoles((prev) => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Current Access List ──────────────────────────────── */}
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Has Access</h3>
              <p className="text-xs text-muted mt-0.5">
                {loading ? '—' : `${grantedUsers.length} user${grantedUsers.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <Package className="w-4 h-4 text-primary" />
          </div>

          <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>
            ) : grantedUsers.length === 0 ? (
              <p className="text-center text-muted text-sm py-10">No users have access yet.</p>
            ) : (
              grantedUsers.map((user) => (
                <div key={user.email} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
                      <p className="text-xs text-muted truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {saving === user.email ? (
                        <Loader2 className="w-3.5 h-3.5 text-muted animate-spin" />
                      ) : (
                        <RoleToggle
                          value={user.role}
                          onChange={(r) => updateRole(user.email, r)}
                          disabled={saving !== null || removing !== null}
                        />
                      )}
                      <button
                        onClick={() => removeUser(user.email)}
                        disabled={removing === user.email || saving !== null}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {removing === user.email ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <UserMinus className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Add Users ─────────────────────────────────────────── */}
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Add Users</h3>
            <p className="text-xs text-muted mt-0.5">Search and grant access with a role</p>
          </div>

          <div className="px-5 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive/40 focus:border-primary"
              />
            </div>
          </div>

          <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
            {availableUsers.length === 0 ? (
              <p className="text-center text-muted text-sm py-8">
                {search ? 'No matches found.' : 'All users already have access.'}
              </p>
            ) : (
              availableUsers.map((user) => (
                <div key={user.email} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 pt-0.5">
                      <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
                      <p className="text-xs text-muted truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <RoleToggle
                        value={getPendingRole(user.email)}
                        onChange={(r) => setPendingRole(user.email, r)}
                        disabled={saving === user.email}
                      />
                      <button
                        onClick={() => handleAdd(user.email)}
                        disabled={saving === user.email}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors disabled:opacity-50"
                      >
                        {saving === user.email ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <UserPlus className="w-3 h-3" />
                        )}
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
